var _excluded = ["actionArea", "behavior", "className", "columnReorder", "columnReorderFooterAlign", "columnReorderInfiniteScroll", "columnReorderPortalContainer", "columnResize", "columns", "component", "controlArea", "customNoItems", "data", "height", "i18n", "loading", "loadingDataLength", "numberOfSelectedItems", "onClearAllFilters", "onColumnReorder", "onDeselectAllItems", "onResizeColumn", "onSelect", "onSelectAll", "onSelectAllItems", "onSelectAllPageItems", "onSort", "pagination", "preferences", "rerenderColumnsOnLoad", "rowSelectAllState", "rowSelector", "showClearAllFilters", "showFilters"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import * as React from 'react';
import { useTheme } from '@veneer/theme';
import useDirection from '../direction/use_direction';
import TableBody from '../table_body';
import TableHead from '../table_head';
import TableHeader from '../table_header';
import TablePagination from '../table_pagination';
import getScrollbarSize from '../utils/get_scrollbar_size';
import useEffect from '../utils/use_effect';
import useForceUpdate from '../utils/use_force_update';
import styles from './use_styles';
import TableContext from './table_context';
import useIntersectionObserver from '../utils/use_intersection_observer';
import Scrollbar from '../scrollbar';
import { DEFAULT_MIN_WIDTH, DEFAULT_WIDTH, SIZE_COMPENSATION } from './defaults';
import tableI18n from './i18n';
import propTypes from './prop_types';
import { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { jsx as ___EmotionJSX } from "@emotion/core";
var defaultComponent = 'table';
var defaultSortableType = 'ascending';
var Table = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  var _preferences$sortBy3, _preferences$sortBy4;
  var {
      actionArea,
      behavior,
      className,
      columnReorder = false,
      columnReorderFooterAlign,
      columnReorderInfiniteScroll = false,
      columnReorderPortalContainer,
      columnResize = false,
      columns: columnsConfig,
      component: Component = defaultComponent,
      controlArea,
      customNoItems,
      data,
      height,
      i18n: i18nProp,
      loading = false,
      loadingDataLength = 20,
      numberOfSelectedItems,
      onClearAllFilters,
      onColumnReorder,
      onDeselectAllItems,
      onResizeColumn,
      onSelect,
      onSelectAll,
      onSelectAllItems,
      onSelectAllPageItems,
      onSort,
      pagination,
      preferences,
      rerenderColumnsOnLoad = false,
      rowSelectAllState,
      rowSelector,
      showClearAllFilters = false,
      showFilters = false
    } = _ref,
    tableProps = _objectWithoutProperties(_ref, _excluded);
  var columnsWidth = React.useRef({});
  var forceUpdate = useForceUpdate();
  var theme = useTheme();
  var i18n = React.useMemo(() => _objectSpread(_objectSpread({}, tableI18n), i18nProp), [i18nProp]);
  var tableWrapperRef = React.useRef(null);
  var tableHeadRef = React.useRef(null);
  var tableBodyRef = React.useRef(null);
  var isColumnResizing = React.useRef(false);
  var [resizeWidths, setResizeWidths] = React.useState([]);
  var {
    isRTL
  } = useDirection();
  var isSinglePage = (behavior === null || behavior === void 0 ? void 0 : behavior.type) === 'singlePage';
  var {
    isIntersecting
  } = useIntersectionObserver(tableWrapperRef);
  var scrollbarSize = React.useMemo(() => typeof document !== 'undefined' ? getScrollbarSize(theme.mode !== 'contrast') : 0, [theme.mode]);
  var setTableBodyHeight = React.useCallback(() => {
    var {
      current: tableWrapper
    } = tableWrapperRef;
    var {
      current: tableHead
    } = tableHeadRef;
    var {
      current: tableBody
    } = tableBodyRef;
    if (tableWrapper && tableHead && tableBody) {
      var hasHorizontalScroll = tableWrapper.scrollWidth > tableWrapper.offsetWidth;
      var bodyHeight = tableWrapper.offsetHeight - tableHead.offsetHeight;
      tableBody.style.height = "".concat(bodyHeight, "px");
      if (hasHorizontalScroll) {
        tableBody.style.paddingBottom = "".concat(scrollbarSize, "px");
      } else {
        tableBody.style.removeProperty('padding-bottom');
      }
    }
  }, [scrollbarSize]);
  var handleWrapperScroll = React.useCallback(() => {
    if (!isSinglePage) return;
    var {
      current: tableWrapper
    } = tableWrapperRef;
    var {
      current: tableBody
    } = tableBodyRef;
    if (!tableWrapper || !tableBody) return;
    var {
      offsetWidth,
      scrollLeft
    } = tableWrapper;
    var absoluteScrollLeft = isRTL ? -scrollLeft : scrollLeft;
    tableBody.style.width = "".concat(offsetWidth + absoluteScrollLeft - SIZE_COMPENSATION, "px");
  }, [isSinglePage, isRTL]);
  var onActionButtonToggle = React.useCallback(function () {
    var open = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
    if (!isSinglePage) return;
    var {
      current: tableWrapper
    } = tableWrapperRef;
    var {
      current: tableBody
    } = tableBodyRef;
    if (!tableWrapper || !tableBody) return;
    var hasVerticalScroll = tableBody.scrollHeight > tableBody.offsetHeight;
    var hasHorizontalScroll = tableWrapper.scrollWidth > tableWrapper.offsetWidth;
    if (open) {
      if (hasVerticalScroll) {
        tableBody.style.paddingRight = "".concat(scrollbarSize, "px");
        tableBody.style.overflowY = 'hidden';
      }
      if (hasHorizontalScroll) {
        tableWrapper.style.overflowX = 'hidden';
      }
    } else {
      tableBody.style.removeProperty('padding-right');
      tableBody.style.removeProperty('overflow-y');
      tableWrapper.style.removeProperty('overflow-x');
    }
  }, [isSinglePage, scrollbarSize]);
  useEffect(() => {
    if (isSinglePage && isIntersecting) {
      setTableBodyHeight();
    }
  }, [behavior === null || behavior === void 0 ? void 0 : behavior.offset, isIntersecting, isSinglePage, pagination, setTableBodyHeight]);
  useEffect(() => {
    forceUpdate();
  }, [forceUpdate, loading]);
  React.useEffect(() => {
    if (!isSinglePage) return undefined;
    var handleResize = () => {
      setTableBodyHeight();
      handleWrapperScroll();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [behavior === null || behavior === void 0 ? void 0 : behavior.offset, isSinglePage, setTableBodyHeight, pagination, handleWrapperScroll]);
  var [indexColumnId, indexColumnType] = React.useMemo(() => {
    var indexColumn = columnsConfig.find(column => column.index);
    if (indexColumn && indexColumn.index && indexColumn.index !== 'none') {
      return [indexColumn.id, indexColumn.index];
    }
    return [undefined, 'none'];
  }, [columnsConfig]);
  var order = React.useMemo(() => {
    var columnsOrderPreferences = preferences && preferences.order;
    var columnsOrderConfig = columnsConfig.map(_ref2 => {
      var {
        id
      } = _ref2;
      return id;
    });
    var handleIndexColumnVisibility = maybeOrder => {
      return indexColumnType === 'hidden' ? maybeOrder.filter(id => id !== indexColumnId) : maybeOrder;
    };
    return handleIndexColumnVisibility(columnsOrderPreferences || columnsOrderConfig);
  }, [columnsConfig, indexColumnId, indexColumnType, preferences]);
  var handleSort = React.useCallback(id => event => {
    var _preferences$sortBy, _preferences$sortBy2;
    var newSortableType = (preferences === null || preferences === void 0 || (_preferences$sortBy = preferences.sortBy) === null || _preferences$sortBy === void 0 ? void 0 : _preferences$sortBy.type) === defaultSortableType && id === (preferences === null || preferences === void 0 || (_preferences$sortBy2 = preferences.sortBy) === null || _preferences$sortBy2 === void 0 ? void 0 : _preferences$sortBy2.id) ? 'descending' : defaultSortableType;
    onSort === null || onSort === void 0 || onSort(event, {
      id,
      type: newSortableType
    });
  }, [onSort, preferences === null || preferences === void 0 ? void 0 : preferences.sortBy]);
  var requiredColumns = columnsConfig.filter(column => column.required);
  var orderedColumns = order.filter(column => columnsConfig.findIndex(c => c.id === column) > -1).map(value => columnsConfig.find(column => column.id === value)).filter(Boolean);
  var columns = React.useMemo(() => [...new Set([...orderedColumns, ...requiredColumns])], [orderedColumns, requiredColumns]);
  var hasFilters = !!columns.find(c => c.filter);
  var hasActions = data ? !!data.find(d => d.rowConfig ? d.rowConfig.action : null) : false;
  var resetColumnWidths = React.useCallback(() => {
    columnsWidth.current = {};
    forceUpdate();
  }, [forceUpdate]);
  var addColumnWidth = React.useCallback((columnId, refWidth) => {
    var {
      current: currentWidths
    } = columnsWidth;
    var preferencesWidths = preferences === null || preferences === void 0 ? void 0 : preferences.width;
    var columnMinWidth = hasFilters ? DEFAULT_WIDTH : DEFAULT_MIN_WIDTH;
    var initialWidth = isSinglePage ? DEFAULT_WIDTH : refWidth;
    var widthIndex = -1;
    if (Array.isArray(preferencesWidths) && preferencesWidths.length > 0) {
      widthIndex = preferencesWidths.findIndex(_ref3 => {
        var {
          columnId: preferencesColumnId
        } = _ref3;
        return columnId === preferencesColumnId;
      });
    }
    var columnPreferences = preferencesWidths === null || preferencesWidths === void 0 ? void 0 : preferencesWidths[widthIndex];
    var columnPreferenceWidth = columnPreferences === null || columnPreferences === void 0 ? void 0 : columnPreferences.width;
    if (!columnPreferenceWidth) {
      if (columnPreferences !== null && columnPreferences !== void 0 && columnPreferences.maxWidth && initialWidth > columnPreferences.maxWidth) {
        initialWidth = columnPreferences.maxWidth;
      }
      if (columnPreferences !== null && columnPreferences !== void 0 && columnPreferences.minWidth && initialWidth < columnPreferences.minWidth) {
        initialWidth = columnPreferences.minWidth;
      } else if (initialWidth < columnMinWidth) {
        initialWidth = columnMinWidth;
      }
    }
    var hasFilter = columns.find(c => c.id === columnId && !!c.filter);
    var width = hasFilter && columnPreferenceWidth !== undefined && columnPreferenceWidth < DEFAULT_WIDTH ? DEFAULT_WIDTH : columnPreferenceWidth;
    if (!columnsWidth.current[columnId]) {
      columnsWidth.current = _objectSpread(_objectSpread({}, currentWidths), {}, {
        [columnId]: {
          width: width || initialWidth
        }
      });
      forceUpdate();
    }
  }, [columns, forceUpdate, hasFilters, isSinglePage, preferences === null || preferences === void 0 ? void 0 : preferences.width]);
  useEffect(() => {
    if (rerenderColumnsOnLoad && !loading) {
      resetColumnWidths();
    }
  }, [resetColumnWidths, rerenderColumnsOnLoad, loading]);
  var tableContextValue = {
    actionArea,
    addColumnWidth,
    columnReorder,
    columnReorderFooterAlign,
    columnReorderInfiniteScroll,
    columnReorderPortalContainer,
    columnResize,
    columns,
    columnsConfig,
    columnsWidth: columnsWidth.current,
    controlArea,
    customNoItems,
    data,
    defaultOrder: preferences ? preferences.defaultOrder : undefined,
    disableResetToDefault: preferences ? preferences.disableResetToDefault : false,
    handleSort,
    hasActions,
    hasFilters,
    headerNoWrap: preferences && preferences.headerNoWrap,
    i18n,
    indexColumnId,
    isColumnResizing,
    isSinglePage,
    loading,
    loadingDataLength,
    numberOfSelectedItems,
    onActionButtonToggle,
    onClearAllFilters,
    onColumnReorder,
    onDeselectAllItems,
    onResetToDefault: preferences ? preferences.onResetToDefault : undefined,
    onResizeColumn,
    onSelect,
    onSelectAll,
    onSelectAllItems,
    onSelectAllPageItems,
    order,
    pagination,
    rerenderColumnsOnLoad,
    resizeWidths,
    rowSelectAllState,
    rowSelector,
    setResizeWidths,
    showClearAllFilters,
    showFilters,
    showResizeTooltip: preferences && preferences.showResizeTooltip || false,
    sortableId: preferences === null || preferences === void 0 || (_preferences$sortBy3 = preferences.sortBy) === null || _preferences$sortBy3 === void 0 ? void 0 : _preferences$sortBy3.id,
    sortableType: preferences === null || preferences === void 0 || (_preferences$sortBy4 = preferences.sortBy) === null || _preferences$sortBy4 === void 0 ? void 0 : _preferences$sortBy4.type,
    tableBodyRef,
    tableHeadRef,
    tableWrapperRef,
    widths: preferences && preferences.width,
    theme
  };
  var style = styles({
    behaviorOffset: behavior === null || behavior === void 0 ? void 0 : behavior.offset,
    height,
    isSinglePage
  });
  return ___EmotionJSX(TableContext.Provider, {
    value: tableContextValue
  }, ___EmotionJSX("div", {
    className: className,
    css: style.root
  }, ___EmotionJSX(TableHeader, null), ___EmotionJSX(Scrollbar, {
    customStyle: style.container,
    onScroll: handleWrapperScroll,
    ref: tableWrapperRef,
    roundMargin: "8px",
    sharpMargin: "2px 6px"
  }, ___EmotionJSX(SkeletonTheme, {
    baseColor: theme.palette.background.disabled,
    highlightColor: theme.palette.stroke.default,
    borderRadius: theme.borderRadius3,
    duration: 2,
    direction: isRTL ? 'rtl' : 'ltr',
    height: "20"
  }, ___EmotionJSX(Component, _extends({}, tableProps, {
    css: style.table,
    ref: ref,
    role: Component === defaultComponent ? null : 'table'
  }), ___EmotionJSX(TableHead, {
    ref: tableHeadRef
  }), ___EmotionJSX(TableBody, {
    ref: tableBodyRef
  })))), pagination ? ___EmotionJSX(TablePagination, {
    pagination: pagination
  }) : undefined));
});
Table.propTypes = propTypes;
Table.displayName = 'Table';
export default Table;